import { POSSIBLE } from './prompts.js';
import { CONSUMABLES } from '../data/consumables.js';

/**
 * Putting something in your body.
 *
 * Nothing about where you are standing. You carry a pen and a joint the way
 * anybody carries a pen and a joint, and the question the game asks is not
 * whether you are allowed but who is looking — which is a question it already
 * knows how to answer.
 */
export const POCKET = Object.freeze(['pen', 'joint']);

/**
 * `unavailable` is whatever is using your hands or your attention this frame —
 * reading the sheet, hanging off something, being under a table — asked rather
 * than held, because it is somebody else's state.
 */
export function createPocket({ impairment, player, hud, autosave, unavailable = () => false }) {
  /**
   * What is actually in it, which is not always both.
   *
   * A new starter's pocket is empty. Things arrive in it from people — a played
   * chapter's `gives` — and from nowhere else, which is how the pen can be
   * something Lil D hands you after a shift without it rather than something you
   * had from the first minute. Every reset that is not a new starter fills it
   * with both, as it always did, so the benchmark's pocket is the one it was
   * measured with.
   */
  let pocket = [...POCKET];
  let pocketAt = 0;

  function offerPocket() {
    if (unavailable()) return [];
    return pocket.map((id, index) => {
      const kind = CONSUMABLES[id];
      return {
        id: `consume-${id}`,
        tier: POSSIBLE,
        // The one you had out last stays the one the key offers first, so the
        // habit of reaching for the same thing survives.
        distance: index === pocketAt ? 0 : 0.01 + index,
        verb: 'take',
        what: `the ${kind.name.toLowerCase()}`,
        run() {
          pocketAt = index;
          impairment.consume(kind.id, player.position);
          hud.say(`${kind.verb}.`, { tone: 'info', seconds: 2 });
          autosave.touch();
        },
      };
    });
  }

  return {
    offer: offerPocket,
    /** What is in it, in the order it went in. */
    get contents() {
      return [...pocket];
    },
    /** The one you last had out. */
    get current() {
      return pocket[pocketAt] ?? null;
    },
    /** Whatever somebody hands you. Nothing arrives twice. */
    give(ids = []) {
      for (const id of ids) if (!pocket.includes(id)) pocket.push(id);
    },
    /** A new starter's. */
    empty() {
      pocket = [];
      pocketAt = 0;
    },
    /** Everybody else's: both, as it always was. */
    fill() {
      pocket = [...POCKET];
      pocketAt = 0;
    },
    /** A save from before the pocket was state is a full one. */
    load(saved) {
      pocket = Array.isArray(saved) ? [...saved] : [...POCKET];
      pocketAt = 0;
    },
    toJSON: () => [...pocket],
  };
}
